/**
 * 输入插件：注册 InputRouter 服务，提供事件驱动的输入分发能力
 */

import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import type { EngineContext } from '@/runtime/EngineContext'
import type { InputConsumer } from '@/shared/types/core'
import type { Plugin } from '@/shared/types/plugin'
import type { ViewRect } from '@/shared/types/view'
import { toViewportLocalPoint } from '@/shared/utils/viewRect'
import { InputRouter } from './InputRouter'
import { PointerLockServiceImpl } from './PointerLockService'
import { ViewEventRouter } from './ViewEventRouter'

const VIEW_ACTIVATION_PRIORITY = 25_000

/** 输入插件：注册 InputRouter 服务，纯事件驱动无帧级处理 */
export class InputPlugin implements Plugin {
  /** 插件名称 */
  readonly name = 'input'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 5
  /** 依赖的服务名列表 */
  readonly dependencies = ['CameraRig', 'ViewRegistry'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['InputRouter', 'ViewEventRouter', 'PointerLockService'] as const

  private context: EngineContext | undefined
  private router: InputRouter | undefined
  private pointerLockService: PointerLockServiceImpl | undefined
  private viewEventRouter: ViewEventRouter | undefined
  private activationConsumer: InputConsumer | null = null
  private _disposed = false

  private toClientViewport(
    containerViewport: { x: number; y: number; width: number; height: number },
    rect: ViewRect
  ) {
    return {
      x: containerViewport.x + rect.x,
      y: containerViewport.y + rect.y,
      width: rect.width,
      height: rect.height,
    }
  }

  private shouldUseContainerViewportAsCompatibilityDefault(): boolean {
    if (!this.context?.hasService('ViewRegistry')) {
      return true
    }

    const viewRegistry = this.context.getService('ViewRegistry')
    const visibleViews = viewRegistry
      .list()
      .filter((view) => view.visible && view.rect.width > 0 && view.rect.height > 0)

    return visibleViews.length === 1 && visibleViews[0]?.id === viewRegistry.primaryViewId
  }

  /**
   * 初始化插件，注册 InputRouter 与 ViewEventRouter 服务。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    const renderInvalidation = context.hasService('RenderInvalidationService')
      ? context.getService('RenderInvalidationService')
      : null
    this.router = renderInvalidation
      ? new InputRouter(context.container, renderInvalidation)
      : new InputRouter(context.container)
    this.pointerLockService = new PointerLockServiceImpl(context.container, context.events)
    this.viewEventRouter = new ViewEventRouter()
    context.registerService('InputRouter', this.router)
    context.registerService('PointerLockService', this.pointerLockService)
    context.registerService('ViewEventRouter', this.viewEventRouter)
  }

  /** 启动插件，连接 ViewEventRouter 并注册活动视图切换消费者 */
  onStart(): void {
    if (!this.context) {
      throw new Error('InputPlugin 未初始化，无法启动')
    }

    // 依赖服务必须在 onStart 之后读取，避免破坏拓扑初始化约束。
    this.context.getService('CameraRig')
    const viewRegistry = this.context.getService('ViewRegistry')
    this.pointerLockService?.bindViewRegistry(viewRegistry)
    this.viewEventRouter?.connect(this.router!, viewRegistry, () => {
      const rect = this.context!.container.getBoundingClientRect()
      return {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      }
    })
    this.router?.setCompatibilityViewportProvider(() => {
      const containerRect = this.context!.container.getBoundingClientRect()
      const containerViewport = {
        x: containerRect.left,
        y: containerRect.top,
        width: containerRect.width,
        height: containerRect.height,
      }

      if (this.shouldUseContainerViewportAsCompatibilityDefault()) {
        return containerViewport
      }

      if (!this.context?.hasService('ViewRegistry')) {
        return null
      }

      const capturedViewId = this.viewEventRouter?.capturedViewId
      if (capturedViewId) {
        const capturedViewRect = this.context.getService('ViewRegistry').get(capturedViewId)?.rect
        return capturedViewRect ? this.toClientViewport(containerViewport, capturedViewRect) : null
      }

      const activeViewRect = this.context.getService('ViewRegistry').getActive()?.rect
      return activeViewRect ? this.toClientViewport(containerViewport, activeViewRect) : null
    })
    this.activationConsumer = {
      name: 'input-active-view-sync',
      priority: VIEW_ACTIVATION_PRIORITY,
      onPointerDown: (event) => {
        const rect = this.context!.container.getBoundingClientRect()
        const hitView = viewRegistry.resolveViewAtScreen(
          toViewportLocalPoint(event.screen, {
            x: rect.left,
            y: rect.top,
          })
        )
        if (hitView && hitView.id !== viewRegistry.getActive()?.id) {
          viewRegistry.setActive(hitView.id)
        }
        return false
      },
    }
    this.router?.addConsumer(this.activationConsumer)
  }

  /** 销毁插件，释放 router 与订阅 */
  onDestroy(): void {
    if (this._disposed) return
    this._disposed = true
    if (this.router && this.activationConsumer) {
      this.router.removeConsumer(this.activationConsumer)
      this.activationConsumer = null
    }
    this.pointerLockService?.dispose()
    this.viewEventRouter?.dispose()
    this.router?.dispose()
  }
}
